/**
 * Active time-on-site. Kernel-owned: runs on every website/activity, started
 * by init alongside the page view.
 *
 * Reported as whole-second deltas that the pipeline sums per session
 * (`analytics:item_duration_sec` → `time_on_page_sec`) — a lost pixel only
 * loses that delta, never the session total. A 30s interval drives the
 * reports, plus a flush on tab-hide / pagehide. A single accrual is capped so
 * device sleep and clock jumps can't inflate the metric.
 *
 * Elapsed time is counted by default, as yoto-web does; `activeTimeOnly` pauses
 * the ticker while the tab is hidden, which measures engaged time instead and is
 * not comparable with yoto-web's figure. Either way a bfcache restore does not
 * count the frozen span — `pagehide` already reported the time before it.
 */

import { track } from './track';
import { CORE_EVENTS } from './coreEvents';

const REPORT_INTERVAL_MS = 30 * 1000;
const MAX_ACCRUAL_MS = 30 * 60 * 1000;

// sanity bounds: sub-second deltas are noise, 30min+ deltas are clock artifacts
const lessThan30Minutes = (v: number): boolean => v > 1 && v < 60 * 30;

let started = false;
let intervalId: ReturnType<typeof setInterval> | null = null;
let detachListeners: (() => void) | null = null;

export function startTimeOnSiteTracking(activeTimeOnly = false): void {
  if (started) return;
  started = true;

  const paused = (): boolean => activeTimeOnly && document.hidden;

  let lastTick: number | null = paused() ? null : Date.now();
  let pendingMs = 0;

  const accrue = (): void => {
    if (lastTick != null) {
      const delta = Date.now() - lastTick;
      if (delta > 0 && delta < MAX_ACCRUAL_MS) pendingMs += delta;
      lastTick = null;
    }
  };

  const reportItemDuration = (): void => {
    accrue();
    const v = Math.ceil(pendingMs / 1000);
    pendingMs = 0;
    if (lessThan30Minutes(v)) {
      track(CORE_EVENTS.itemDuration, { v });
    }
    if (!paused()) lastTick = Date.now();
  };

  // Hiding always flushes — the delta is reported while a beacon can still leave
  // — but the clock only stops when counting active time.
  const onVisibilityChange = (): void => {
    if (document.hidden) {
      reportItemDuration();
    } else if (lastTick == null) {
      lastTick = Date.now();
    }
  };
  const onPageShow = (event: Event): void => {
    if ((event as PageTransitionEvent).persisted) {
      lastTick = paused() ? null : Date.now();
    }
  };

  intervalId = setInterval(reportItemDuration, REPORT_INTERVAL_MS);
  document.addEventListener('visibilitychange', onVisibilityChange);
  // no unload/beforeunload — they break bfcache; sendBeacon survives pagehide
  window.addEventListener('pagehide', reportItemDuration);
  window.addEventListener('pageshow', onPageShow);

  detachListeners = () => {
    document.removeEventListener('visibilitychange', onVisibilityChange);
    window.removeEventListener('pagehide', reportItemDuration);
    window.removeEventListener('pageshow', onPageShow);
  };
}

/** Test-only */
export function _resetTimeOnSiteForTests(): void {
  started = false;
  if (intervalId != null) clearInterval(intervalId);
  intervalId = null;
  detachListeners?.();
  detachListeners = null;
}
